import { useState, useEffect } from 'react';
import Fuse from 'fuse.js';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { convertPodcastUrl } from '../config/environment';

export const usePodcasts = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fuse, setFuse] = useState(null);

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        const response = await fetch('/data/podcasts.json');
        const data = await response.json();
        
        // Handle new metadata structure with summary and episodes
        let episodesData = data;
        let summaryData = null;
        
        if (data.summary && data.episodes) {
          episodesData = data.episodes;
          summaryData = data.summary;
          setSummary(summaryData);
        }
        
        // Convert S3 URLs to environment-appropriate URLs
        const processedData = episodesData.map(podcast => ({
          ...podcast,
          s3Url: convertPodcastUrl(podcast.s3Url)
        }));
        
        setPodcasts(processedData);
        
        // Initialize Fuse.js for fuzzy search
        const fuseInstance = new Fuse(processedData, {
          keys: [
            { name: 'title', weight: 0.4 },
            { name: 'description', weight: 0.3 },
            { name: 'participants', weight: 0.2 },
            { name: 'tags', weight: 0.1 }
          ],
          threshold: 0.3,
          includeScore: true
        });
        setFuse(fuseInstance);
      } catch (error) {
        console.error('Error fetching podcasts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPodcasts();
  }, []);

  const searchPodcasts = (searchTerm) => {
    if (!searchTerm.trim() || !fuse) {
      return podcasts;
    }

    const results = fuse.search(searchTerm);
    return results.map(result => result.item);
  };

  const filterByDate = (podcastList, dateRange) => {
    if (!dateRange.start && !dateRange.end) {
      return podcastList;
    }

    return podcastList.filter(podcast => {
      const podcastDate = parseISO(podcast.date);
      
      if (dateRange.start && dateRange.end) {
        const startDate = parseISO(dateRange.start);
        const endDate = parseISO(dateRange.end);
        return !isBefore(podcastDate, startDate) && !isAfter(podcastDate, endDate);
      }
      
      if (dateRange.start) {
        const startDate = parseISO(dateRange.start);
        return !isBefore(podcastDate, startDate);
      }
      
      if (dateRange.end) {
        const endDate = parseISO(dateRange.end);
        return !isAfter(podcastDate, endDate);
      }
      
      return true;
    });
  };

  const filterByParticipants = (podcastList, participantNames) => {
    if (participantNames.length === 0) {
      return podcastList;
    }

    return podcastList.filter(podcast => 
      participantNames.some(name => 
        podcast.participants.some(participant => 
          participant.toLowerCase().includes(name.toLowerCase())
        )
      )
    );
  };

  const getPodcastById = (id) => {
    return podcasts.find(podcast => podcast.id === id);
  };

  const getPodcastsByYear = (year) => {
    return podcasts.filter(podcast => 
      new Date(podcast.date).getFullYear() === year
    );
  };

  const getUniqueParticipants = () => {
    const allParticipants = podcasts.flatMap(podcast => podcast.participants);
    return [...new Set(allParticipants)].sort();
  };

  const getUniqueTags = () => {
    const allTags = podcasts.flatMap(podcast => podcast.tags || []);
    return [...new Set(allTags)].sort();
  };

  return {
    podcasts,
    summary,
    loading,
    searchPodcasts,
    filterByDate,
    filterByParticipants,
    getPodcastById,
    getPodcastsByYear,
    getUniqueParticipants,
    getUniqueTags
  };
};